import React, { useState } from 'react';
import { useTheme } from '../assets/ThemeContext';
import SurfaceStyleSettings from '../components/SurfaceStyleSettings';

const THEMES = [
  { id: 'deep_glass', label: 'Deep Glass', desc: 'Premium aviation dark theme with glass surfaces.', color: '#2C72AE', gold: '#C6A744' },
  { id: 'tsela',      label: 'Tsela Original', desc: 'Classic high-contrast aviation interface.', color: '#6094ea', gold: null },
  { id: 'argon',      label: 'Argon Modern', desc: 'Sleek professional dashboard aesthetic.', color: '#5e72e4', gold: null },
  { id: 'datta',      label: 'Datta Classic', desc: 'Balanced and clean corporate design.', color: '#04A9F5', gold: null },
  { id: 'cosmic',     label: 'Cosmic Pure', desc: 'Ultra-modern glass with cosmic accents.', color: '#2b9aff', gold: null },
];

const TABS = [
  { key: 'theme', label: 'Theme' },
  { key: 'surfaces', label: 'Surface Visualizer' },
  { key: 'about', label: 'About' },
];

export default function Settings() {
  const { themeId, setThemeId, isDark, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('theme');

  return (
    <div style={{ padding: '32px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 22 }}>

      {/* Page header */}
      <div>
        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>Settings</div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4, fontWeight: 300 }}>
          Personalize your Tsela OAS experience
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: 0 }}>
        {TABS.map(tab => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: active ? '2px solid #5F98C4' : '2px solid transparent',
                padding: '10px 20px',
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                color: active ? '#5F98C4' : 'var(--text-tertiary)',
                cursor: 'pointer',
                marginBottom: -1,
                transition: 'color 0.15s',
                letterSpacing: '0.2px',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Theme tab */}
      {activeTab === 'theme' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 22 }}>

          {/* Theme selection */}
          <div className="dg-panel" style={{ padding: '24px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 16 }}>
              Interface Theme
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {THEMES.map(t => {
                const active = themeId === t.id;
                return (
                  <div
                    key={t.id}
                    onClick={() => setThemeId(t.id)}
                    style={{
                      padding: '14px 16px',
                      borderRadius: 12,
                      border: `1px solid ${active ? 'rgba(95,152,196,0.4)' : 'rgba(255,255,255,0.06)'}`,
                      background: active ? 'rgba(44,114,174,0.12)' : 'rgba(255,255,255,0.02)',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 14,
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                      background: t.gold
                        ? `linear-gradient(135deg, ${t.color}, ${t.gold})`
                        : `linear-gradient(135deg, ${t.color}, ${t.color}99)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 14, fontWeight: 800,
                      border: active ? '2px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.1)',
                    }}>
                      {t.label[0]}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: active ? 600 : 400, color: active ? '#5F98C4' : 'var(--text-primary)' }}>
                        {t.label}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t.desc}</div>
                    </div>
                    {active && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="#5F98C4" strokeWidth="2.5" width="16" height="16" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Display mode */}
          <div className="dg-panel" style={{ padding: '24px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 16 }}>
              Display Mode
            </div>
            <div
              onClick={toggleTheme}
              style={{
                padding: '18px',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.06)',
                background: 'rgba(255,255,255,0.02)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: isDark ? 'rgba(44,114,174,0.18)' : 'rgba(255,210,80,0.15)',
                  border: `1px solid ${isDark ? 'rgba(95,152,196,0.3)' : 'rgba(255,210,80,0.3)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                }}>
                  {isDark ? '🌙' : '☀️'}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {isDark ? 'Dark Mode' : 'Light Mode'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    Switch between light and dark versions
                  </div>
                </div>
              </div>
              <div style={{
                width: 44, height: 24, borderRadius: 24,
                background: isDark ? '#C6A744' : 'rgba(255,255,255,0.2)',
                position: 'relative', flexShrink: 0,
                transition: 'background 0.3s', boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.2)',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3, left: isDark ? 23 : 3,
                  transition: 'left 0.3s', boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Surface Visualizer tab */}
      {activeTab === 'surfaces' && <SurfaceStyleSettings />}

      {/* About tab */}
      {activeTab === 'about' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 22 }}>
          <div className="dg-panel" style={{ padding: '24px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 16 }}>
              About
            </div>
            {[
              { label: 'Version', value: 'v2.5.0 · Quantum' },
              { label: 'Standard', value: 'ICAO Annex 14 (9th Ed 2022)' },
              { label: 'Reference', value: 'EUR Doc 015 (3rd Ed 2015)' },
              { label: 'Procedures', value: 'PANS-OPS Doc 8168' },
            ].map((r, i, arr) => (
              <div key={r.label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                paddingBottom: i < arr.length - 1 ? 10 : 0,
                marginBottom: i < arr.length - 1 ? 10 : 0,
                borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.label}</span>
                <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
